import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { getErrorMessage } from '../../utils/errorHandler';

import planesAPI, {
  CreatedPlans,
  DataPayload,
  GetPlanPreviewData,
  Plan,
  PlanData,
  RegisterPlanPayload,
  VistaPreviaPlanes,
} from './planesAPI';

interface PlanesState {
  status: 'loading' | 'idle' | 'error' | 'success';
  error?: string;
  list?: Plan[];
  plans?: CreatedPlans[];
  message?: string;
}

export const initialState: PlanesState = {
  status: 'idle',
};

export const getPlanesByResource = createAsyncThunk<
  Plan[],
  PlanData,
  { rejectValue: string }
>('imponibles/planes', async (data, thunkAPI) => {
  try {
    const response = await planesAPI.getPlanesByResource(data);

    if (response.status === 'error') {
      return thunkAPI.rejectWithValue(response.error);
    }

    return response.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(getErrorMessage(err));
  }
});

export const getPlanesByPeriod = createAsyncThunk<
  CreatedPlans[],
  DataPayload,
  { rejectValue: string }
>('plans/created', async (data, thunkAPI) => {
  try {
    const response = await planesAPI.getPlanesByPeriod(data);

    if (response.status === 'error') {
      return thunkAPI.rejectWithValue(response.error);
    }

    return response.data;
  } catch (err) {
    console.error(err);
    return thunkAPI.rejectWithValue(getErrorMessage(err));
  }
});

export const registerPlan = createAsyncThunk<
  CreatedPlans[],
  RegisterPlanPayload,
  { rejectValue: string }
>('plans/creation', async (data, thunkAPI) => {
  try {
    const response = await planesAPI.registerPlan(data);

    if (response.status === 'error') {
      return thunkAPI.rejectWithValue(response.error);
    }

    return response.data;
  } catch (err) {
    console.error(err);
    return thunkAPI.rejectWithValue(getErrorMessage(err));
  }
});

export const getPlanpdf = createAsyncThunk<
  CreatedPlans[],
  RegisterPlanPayload,
  { rejectValue: string }
>('plans/pdf', async (data, thunkAPI) => {
  try {
    const response = await planesAPI.getPlanPdf(data);

    if (response.status === 'error') {
      return thunkAPI.rejectWithValue(response.error);
    }

    return response.data;
  } catch (err) {
    console.error(err);
    return thunkAPI.rejectWithValue(getErrorMessage(err));
  }
});

export const getPlanPreview = createAsyncThunk<
  VistaPreviaPlanes,
  GetPlanPreviewData,
  { rejectValue: string }
>('plans/preview', async (data, thunkAPI) => {
  try {
    const response = await planesAPI.getPlanPreview(data);

    if (response.status === 'error') {
      return thunkAPI.rejectWithValue(response.error);
    }

    return response.data;
  } catch (err) {
    console.error(err);
    return thunkAPI.rejectWithValue(getErrorMessage(err));
  }
});

export const PlanesState = createSlice({
  name: 'PlanesState',
  initialState,
  reducers: {
    cleanPlanState() {
      return {
        ...initialState,
        list: [],
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getPlanesByResource.pending, state => {
        state.status = 'loading';
        state.list = undefined;
      })
      .addCase(getPlanesByResource.fulfilled, (state, action) => {
        state.status = 'success';
        state.list = action.payload;
      })
      .addCase(getPlanesByResource.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload;
      });
    builder
      .addCase(getPlanesByPeriod.pending, state => {
        state.status = 'loading';
        state.plans = undefined;
      })
      .addCase(getPlanesByPeriod.fulfilled, (state, action) => {
        state.status = 'idle';
        state.plans = action.payload;
      })
      .addCase(getPlanesByPeriod.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload;
      });
    builder
      .addCase(registerPlan.pending, state => {
        state.status = 'loading';
        state.plans = undefined;
      })
      .addCase(registerPlan.fulfilled, state => {
        state.status = 'success';
        state.message = 'Plan de pago generado con éxito';
      })
      .addCase(registerPlan.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload;
      });
    builder
      .addCase(getPlanpdf.pending, state => {
        state.status = 'loading';
        state.plans = undefined;
      })
      .addCase(getPlanpdf.fulfilled, state => {
        state.status = 'success';
        state.message = 'Plan de pago generado con éxito';
      })
      .addCase(getPlanpdf.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload;
      });
  },
});

export const getPlanesList = (state: RootState) => state.planes.list ?? [];
export const getCreatedPlansList = (state: RootState) => state.planes.plans ?? [];
export const planStatus = (state: RootState) => state.planes.status;
export const planMessage = (state: RootState) => state.planes.message;
export const planError = (state: RootState) => state.planes.error;
export const { cleanPlanState } = PlanesState.actions;

export default PlanesState.reducer;
